import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';
import type { PieceCategory } from '../../convex/types';
import type { PieceLanguage } from '@/utils/pieceLanguage';

/**
 * A piece sits at one of two parallel placements — a difficulty (song and
 * exercise pieces) or a lesson (lesson pieces) — and the ids of both are
 * opaque strings. The `difficulty:` / `lesson:` prefix is what tells the
 * predicate which field a filter value refers to, so one select can cover the
 * whole "Difficulty / Lesson" column instead of a difficulty-only filter that
 * leaves lesson pieces unfilterable.
 */
export type PiecePlacementFilter = 'all' | `difficulty:${string}` | `lesson:${string}`;

/** The two parallel scales a piece can be placed on. */
export type PlacementScale = 'difficulty' | 'lesson';

/**
 * The scale a category's pieces are placed on — `null` for `all`, where both
 * still apply. Song and exercise pieces sit at a difficulty and lesson pieces
 * in a lesson, so choosing a category rules the other scale out entirely.
 */
export function placementScaleFor(category: PieceCategory | 'all'): PlacementScale | null {
  if (category === 'all') return null;
  return category === 'lesson' ? 'lesson' : 'difficulty';
}

/** The fields the filters read — any piece list carrying them can be filtered. */
type FilterablePiece = {
  _id: string;
  name?: string | null;
  category?: string | null;
  language?: string | null;
  difficultyId?: string | null;
  lessonId?: string | null;
  genre?: string[] | null;
  difficultyStars?: number | null;
};

/**
 * Category / language / placement/ title / genre / stars filter state for a
 * piece table, plus the narrowed list. Pair with `PieceFilterBar`, which
 * renders the search box and selects.
 *
 * Plain refs: the views owning these tables are `<keep-alive>`d, so the
 * selections survive a trip to a detail view as ordinary component state.
 */
export function usePieceFilters<T extends FilterablePiece>(pieces: MaybeRefOrGetter<T[] | undefined>) {
  const titleFilter = ref('');
  const categoryFilter = ref<PieceCategory | 'all'>('all');
  const languageFilter = ref<PieceLanguage | 'all'>('all');
  const placementFilter = ref<PiecePlacementFilter>('all');
  const genreFilter = ref<string>('all');
  const starsFilter = ref<number | 'all'>('all');

  // The select drops the ruled-out scale's options once a category is chosen,
  // so a placement left on that scale would be an invisible filter emptying the
  // table.
  watch(categoryFilter, (category) => {
    const scale = placementScaleFor(category);
    if (scale && !placementFilter.value.startsWith(scale)) placementFilter.value = 'all';
  });

  const filteredPieces = computed(() => {
    const title = titleFilter.value.trim().toLowerCase();
    return (toValue(pieces) ?? []).filter(
      (piece) =>
        (!title || (piece.name ?? '').toLowerCase().includes(title)) &&
        (categoryFilter.value === 'all' || piece.category === categoryFilter.value) &&
        (languageFilter.value === 'all' || piece.language === languageFilter.value) &&
        matchesPlacement(piece, placementFilter.value) &&
        (genreFilter.value === 'all' || (piece.genre ?? []).includes(genreFilter.value)) &&
        (starsFilter.value === 'all' || piece.difficultyStars === starsFilter.value)
    );
  });

  // Genre and stars are freeform per-piece values, so their filter options
  // are whatever is actually in use across the full list this composable was
  // handed — not just the (already-filtered) rows currently on screen.
  const genreOptions = computed(() => {
    const genres = new Set<string>();
    for (const piece of toValue(pieces) ?? []) {
      for (const genre of piece.genre ?? []) genres.add(genre);
    }
    return [...genres].sort((a, b) => a.localeCompare(b));
  });
  const starsOptions = computed(() => {
    const stars = new Set<number>();
    for (const piece of toValue(pieces) ?? []) {
      if (typeof piece.difficultyStars === 'number') stars.add(piece.difficultyStars);
    }
    return [...stars].sort((a, b) => a - b);
  });

  return {
    titleFilter,
    categoryFilter,
    languageFilter,
    placementFilter,
    genreFilter,
    starsFilter,
    filteredPieces,
    genreOptions,
    starsOptions,
  };
}

function matchesPlacement(piece: FilterablePiece, filter: PiecePlacementFilter) {
  if (filter === 'all') return true;
  const [scale, id] = filter.split(':');
  return scale === 'difficulty' ? piece.difficultyId === id : piece.lessonId === id;
}
